import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Brain, ArrowRight, Check, Loader2, Video } from 'lucide-react';
import { supabase } from '../hooks/useAuth';
import OctopusLogo from '../components/common/OctopusLogo';

export default function Landing() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [artistType, setArtistType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const { error: dbError } = await supabase.from('beta_signups').insert({
        email: email.toLowerCase().trim(),
        name: name.trim(),
        artist_type: artistType,
        source: 'landing_page',
      });

      if (dbError) {
        if (dbError.code === '23505') {
          setError('This email is already on the waitlist!');
        } else {
          throw dbError;
        }
      } else {
        setIsSubmitted(true);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: Brain,
      title: 'AI That Learns You',
      description: 'Upload your music and Jellybop learns your unique style, vibe, and aesthetic.',
    },
    {
      icon: Video,
      title: 'Generate Videos',
      description: 'Create TikTok/Reels-ready music videos that match your artistic vision.',
    },
    {
      icon: Zap,
      title: 'Instant Results',
      description: 'From track upload to finished video in minutes, not days.',
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <OctopusLogo size="md" />
            <span className="text-xl font-bold">Jellybop</span>
          </div>
          <Link
            to="/login"
            className="text-sm text-zinc-400 hover:text-white transition-colors"
          >
            Sign In
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-jelly-500/10 text-jelly-400 text-sm mb-6">
            <OctopusLogo size="sm" />
            Now accepting beta users
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
            AI-Powered Music Videos<br />That Learn Your Style
          </h1>
          <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
            Jellybop creates TikTok and Reels-ready music videos that capture your unique artistic identity.
            The more you use it, the better it understands you.
          </p>

          {/* Beta Signup Form */}
          {isSubmitted ? (
            <div className="max-w-md mx-auto bg-green-900/20 border border-green-800 rounded-xl p-6">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">You're on the list!</h3>
              <p className="text-zinc-400">
                We'll reach out soon with your beta access. Keep an eye on your inbox.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg focus:outline-none focus:border-jelly-500 transition-colors"
              />
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg focus:outline-none focus:border-jelly-500 transition-colors"
              />
              <select
                value={artistType}
                onChange={(e) => setArtistType(e.target.value)}
                required
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg focus:outline-none focus:border-jelly-500 transition-colors text-zinc-400"
              >
                <option value="">What type of artist are you?</option>
                <option value="musician">Musician / Producer</option>
                <option value="band">Band</option>
                <option value="dj">DJ</option>
                <option value="label">Record Label</option>
                <option value="manager">Artist Manager</option>
                <option value="other">Other</option>
              </select>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-jelly-500 to-purple-600 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    Join the Beta
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
              <p className="text-xs text-zinc-500">
                Free during beta. No credit card required.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-jelly-500/20 to-purple-600/20 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-jelly-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it learns */}
      <section className="py-20 px-4 border-t border-zinc-800 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">The More You Use It, The Better It Gets</h2>
          <p className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto">
            Jellybop builds a unique AI persona for each artist. It learns from your music,
            your feedback, and your style preferences to create videos that feel authentically you.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Upload Your Music</h3>
              <p className="text-zinc-400 text-sm">
                Our AI analyzes tempo, mood, genre, and lyrics to understand your sound.
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Define Your Vision</h3>
              <p className="text-zinc-400 text-sm">
                Describe your aesthetic or upload reference images. The AI builds your visual identity.
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Generate Videos</h3>
              <p className="text-zinc-400 text-sm">
                Create platform-ready videos that match your music and style automatically.
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Approve & Learn</h3>
              <p className="text-zinc-400 text-sm">
                Your feedback teaches the AI. It remembers what you like and improves over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 border-t border-zinc-800">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Create?</h2>
          <p className="text-zinc-400 mb-8">
            Join the beta and start generating music videos that capture your unique style.
          </p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-jelly-500 to-purple-600 rounded-lg font-semibold hover:opacity-90 transition-opacity"
          >
            Join the Beta
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <OctopusLogo size="sm" />
            <span className="font-semibold">Jellybop</span>
          </div>
          <p className="text-sm text-zinc-500">
            &copy; {new Date().getFullYear()} Jellybop. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
